import React from 'react';
import {
  Box, Typography, Paper, CircularProgress, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Checkbox, Snackbar, Alert, Divider, Chip, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import LinkIcon from '@mui/icons-material/Link';

// ksm_backend/ksm_frontend 포팅(2026-10-19, bito 3단계) — 메뉴 관리(트리 + 추가/수정/삭제/이력) 화면.

const MenuManagementView = ({
  menuList,
  parentMenus,
  loading,
  selectedMenu,
  openDialog,
  dialogMode,
  historyMode,
  historyData,
  formData,
  snackbar,
  expanded,
  handleOpenAddDialog,
  handleAddChildMenu,
  handleOpenEditDialog,
  handleCloseDialog,
  handleFormChange,
  handleSubmit,
  handleDelete,
  handleOpenHistory,
  handleCloseHistory,
  handleToggle,
  handleViewMenu,
  handleCloseSnackbar,
}) => {

  if (!menuList || !parentMenus) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">메뉴 데이터가 없습니다.</Typography>
        </Paper>
      </Box>
    );
  }

  // 상위 메뉴 선택용 (최상위 메뉴만, 수정 시 자기 자신 제외)
  const parentOptions = parentMenus.filter(
    (m) => !formData.menuUuid || m.menuUuid !== formData.menuUuid
  );

  // 액션 버튼 렌더링
  const ActionButtons = ({ menu, isParent }) => (
    <Box sx={{ display: 'flex', gap: 0.5, opacity: 0.7, '&:hover': { opacity: 1 } }}>
      {isParent && (
        <Tooltip title="하위 메뉴 추가">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleAddChildMenu(menu); }}>
            <AddIcon fontSize="small" color="primary" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="수정">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(menu); }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="변경 이력">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenHistory(menu.menuUuid); }}>
          <HistoryIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="삭제">
        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(menu.menuUuid); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
      {/* 왼쪽: 메뉴 트리 */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            mb: 1.5,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            최상위 메뉴 {parentMenus.length}개
          </Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenAddDialog}>
            최상위 메뉴 추가
          </Button>
        </Box>

        <Paper elevation={1} sx={{ minHeight: 400 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : parentMenus.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <Typography color="text.secondary">등록된 메뉴가 없습니다.</Typography>
            </Box>
          ) : (
            <Box sx={{ p: 1 }}>
              {parentMenus.map((menu) => {
                const children = menuList.filter((child) => child.parentUuid === menu.menuUuid);
                const isExpanded = expanded?.includes(menu.menuUuid);
                const isSelected = selectedMenu?.menuUuid === menu.menuUuid;

                return (
                  <Box key={menu.menuUuid} sx={{ mb: 0.5 }}>
                    {/* 상위 메뉴 행 */}
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', p: 1,
                        borderRadius: 1, cursor: 'pointer',
                        bgcolor: isSelected ? 'primary.50' : 'transparent',
                        border: isSelected ? '1px solid' : '1px solid transparent',
                        borderColor: isSelected ? 'primary.200' : 'transparent',
                        '&:hover': { bgcolor: isSelected ? 'primary.50' : 'grey.50' },
                      }}
                      onClick={() => handleViewMenu(menu)}
                    >
                      {children.length > 0 ? (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const newExpanded = isExpanded
                              ? expanded.filter((id) => id !== menu.menuUuid)
                              : [...(expanded || []), menu.menuUuid];
                            handleToggle(null, newExpanded);
                          }}
                        >
                          {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                        </IconButton>
                      ) : (
                        <Box sx={{ width: 34 }} />
                      )}
                      <Typography
                        variant="body1"
                        sx={{
                          flexGrow: 1,
                          minWidth: 0,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {menu.menuTitle}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({menu.menuId})
                        </Typography>
                      </Typography>
                      {children.length > 0 && (
                        <Chip label={`${children.length}`} size="small" variant="outlined" sx={{ mr: 1, height: 20, fontSize: '0.7rem' }} />
                      )}
                      <ActionButtons menu={menu} isParent />
                    </Box>

                    {/* 하위 메뉴 목록 */}
                    {isExpanded && children.length > 0 && (
                      <Box sx={{ ml: 5, borderLeft: '2px solid #e0e0e0', pl: 1 }}>
                        {children.map((child) => {
                          const isChildSelected = selectedMenu?.menuUuid === child.menuUuid;
                          return (
                            <Box
                              key={child.menuUuid}
                              sx={{
                                display: 'flex', alignItems: 'center', p: 0.8, my: 0.3,
                                borderRadius: 1, cursor: 'pointer',
                                bgcolor: isChildSelected ? 'primary.50' : 'transparent',
                                border: isChildSelected ? '1px solid' : '1px solid transparent',
                                borderColor: isChildSelected ? 'primary.200' : 'transparent',
                                '&:hover': { bgcolor: isChildSelected ? 'primary.50' : 'grey.50' },
                              }}
                              onClick={() => handleViewMenu(child)}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  flexGrow: 1,
                                  minWidth: 0,
                                  ml: 1,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {child.menuTitle}
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  ({child.menuId})
                                </Typography>
                              </Typography>
                              {child.menuLink && (
                                <Chip
                                  icon={<LinkIcon sx={{ fontSize: 14 }} />}
                                  label={child.menuLink}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    display: { xs: 'none', sm: 'inline-flex' },
                                    mr: 1,
                                    height: 20,
                                    fontSize: '0.7rem',
                                    maxWidth: 150,
                                  }}
                                />
                              )}
                              <ActionButtons menu={child} isParent={false} />
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      </Box>

      {/* 오른쪽: 선택된 메뉴 상세 */}
      {selectedMenu && (
        <Paper
          elevation={1}
          sx={{
            width: { xs: '100%', md: 320 },
            flexShrink: 0,
            p: 2,
            alignSelf: { xs: 'stretch', md: 'flex-start' },
            mt: { xs: 0, md: 5 },
          }}
        >
          <Typography variant="subtitle2" gutterBottom>메뉴 상세</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <DetailRow label="메뉴 ID" value={selectedMenu.menuId} />
            <DetailRow label="메뉴명" value={selectedMenu.menuTitle} />
            <DetailRow label="링크" value={selectedMenu.menuLink || '-'} />
            <DetailRow label="아이콘" value={selectedMenu.menuIcon || '-'} />
            <DetailRow label="정렬순서" value={selectedMenu.menuOrder ?? 0} />
            <DetailRow label="상위 메뉴" value={
              selectedMenu.parentUuid
                ? menuList.find((m) => m.menuUuid === selectedMenu.parentUuid)?.menuTitle || '-'
                : '(최상위)'
            } />
            <Divider />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={selectedMenu.menuExpand ? '확장' : '접힘'} size="small" color={selectedMenu.menuExpand ? 'primary' : 'default'} variant="outlined" />
              <Chip label={selectedMenu.menuChecked ? '활성' : '비활성'} size="small" color={selectedMenu.menuChecked ? 'success' : 'default'} variant="outlined" />
              {selectedMenu.menuAdmin && <Chip label="관리자" size="small" color="warning" variant="outlined" />}
            </Box>
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" fullWidth onClick={() => handleOpenEditDialog(selectedMenu)}>
              수정
            </Button>
            <Button size="small" variant="outlined" color="error" fullWidth onClick={() => handleDelete(selectedMenu.menuUuid)}>
              삭제
            </Button>
          </Box>
        </Paper>
      )}

      {/* 추가/수정 다이얼로그 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogMode === 'add' ? '메뉴 추가' : '메뉴 수정'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="menuId" label="메뉴 ID" type="number" size="small"
                value={formData.menuId || ''} onChange={handleFormChange}
                required sx={{ width: 120 }}
                disabled={dialogMode === 'edit'}
              />
              <TextField
                name="menuTitle" label="메뉴명" size="small" fullWidth
                value={formData.menuTitle || ''} onChange={handleFormChange}
                required
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="menuLink" label="메뉴 링크" size="small" fullWidth
                value={formData.menuLink || ''} onChange={handleFormChange}
                placeholder="예: codemng, projectmng"
              />
              <TextField
                name="menuIcon" label="아이콘" size="small"
                value={formData.menuIcon || ''} onChange={handleFormChange}
                sx={{ width: 150 }}
                placeholder="예: common"
              />
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel>상위 메뉴</InputLabel>
              <Select
                name="parentUuid" label="상위 메뉴"
                value={formData.parentUuid || ''}
                onChange={handleFormChange}
              >
                <MenuItem value=""><em>없음 (최상위 메뉴)</em></MenuItem>
                {parentOptions.map((m) => (
                  <MenuItem key={m.menuUuid} value={m.menuUuid}>
                    {m.menuTitle} ({m.menuId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              name="menuOrder" label="정렬순서" type="number" size="small"
              value={formData.menuOrder || ''} onChange={handleFormChange}
              sx={{ width: 120 }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={formData.menuExpand || false} onChange={handleFormChange} name="menuExpand" />}
                label="펼침"
              />
              <FormControlLabel
                control={<Checkbox checked={formData.menuChecked || false} onChange={handleFormChange} name="menuChecked" />}
                label="활성"
              />
              <FormControlLabel
                control={<Checkbox checked={formData.menuAdmin || false} onChange={handleFormChange} name="menuAdmin" />}
                label="관리자 전용"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'add' ? '추가' : '수정'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 이력 다이얼로그 */}
      <Dialog open={historyMode} onClose={handleCloseHistory} maxWidth="md" fullWidth>
        <DialogTitle>메뉴 변경 이력</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : !historyData || historyData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>변경 이력이 없습니다.</Typography>
          ) : (
            historyData.map((h, i) => (
              <Box key={h.hisUuid || i} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Chip
                    label={h.cngType === 'I' ? '생성' : h.cngType === 'U' ? '수정' : '삭제'}
                    size="small"
                    color={h.cngType === 'I' ? 'success' : h.cngType === 'U' ? 'info' : 'error'}
                  />
                  <Typography variant="body2" fontWeight="bold">
                    {h.menuTitle} ({h.menuId})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {h.crtDt || '-'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  링크: {h.menuLink || '-'} | 순서: {h.menuOrder ?? '-'}
                </Typography>
                {i < historyData.length - 1 && <Divider sx={{ mt: 1.5 }} />}
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistory}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar open={snackbar?.open || false} autoHideDuration={4000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar?.severity || 'info'} sx={{ width: '100%' }}>
          {snackbar?.message || ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value}</Typography>
  </Box>
);

export default MenuManagementView;
